import { useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isDefined } from "src/utils";
import { ZodObject, ZodRawShape, z } from "zod";

/**
 * Provised a useQueryString-like hook where the state is constantly reflected in the URL via URL Search/Query Params.
 *
 * Uses Zod to validate the state and the Query String and return a strongly typed sub set state fields.
 * @param schema Zod Object Schema
 */
const useZodQueryString = <T extends ZodRawShape>(
  schema: ZodObject<T>,
): [z.infer<typeof schema>, (state: Partial<z.infer<typeof schema>>) => void] => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  /** Gather up/initialize from active Query String in the URL. Recalc any time QueryString changes. */
  const parsedFields = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return schema.parse([...queryParams.entries()].toObject());
  }, [location.search, schema]);

  // Update the Query String when the state changes
  const setState = useCallback(
    (newState: Partial<z.infer<typeof schema>>) => {
      const newQueryParams = new URLSearchParams(locationRef.current.search);
      for (const [key, value] of Object.entries(newState)) {
        if (!isDefined(value)) {
          newQueryParams.delete(key);
        } else {
          newQueryParams.set(key, String(value));
        }
      }
      navigate({ search: newQueryParams.toString() }, { replace: true, state: locationRef.current.state });
    },
    [navigate],
  );

  return [parsedFields, setState];
};

export default useZodQueryString;
